import { useEffect, useState } from 'react'
import {
  Box,
  Chip,
  Modal,
  Button,
  Tooltip,
  Divider,
  MenuItem,
  Accordion,
  TextField,
  Typography,
  IconButton,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { Stack } from '@mui/system'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import { IUser, IRole } from '../../../../../shared/types/api/user.type'
import { ITier } from '../../../../../shared/types/api/tier.type'
import { useToast } from '../../../../contexts/toast.context'
import { SelectWithSearch } from '../../../shared/SearchSelect'
import { updateUserData } from '../../../../services/user.service'
import { getAllTiers } from '../../../../services/tier.service'
import { thru } from '../../../../utils/functions'

import styles from './styles.module.scss'

// Helpers for making user permissions preview more user-friendly
const scopeShort = (s?: any) => {
  if (!s) return '—'
  if (s.title?.trim()) return s.title
  const keys = ['f200', 'f201', 'f202', 'f203', 'f204', 'f205', 'f206', 'f207', 'f208', 'f302', 'f910']
  const parts = keys
    .filter((k) => s[k] !== null && s[k] !== undefined && String(s[k]).trim() !== '')
    .map((k) => `${k.replace('f_', '')}:${s[k]}`)
  return parts.length ? parts.slice(0, 4).join(' • ') : '—'
}

const scopeFull = (s?: any) => {
  if (!s) return 'No scope'
  const entries = Object.entries(s)
    .filter(
      ([k, v]) => k === 'title' || (k.startsWith('f_') && v !== null && v !== undefined && String(v).trim() !== ''),
    )
    .map(([k, v]) => `${k}: ${v}`)
  return entries.length ? entries.join('\n') : 'No scope'
}

const actionColor = (a: string) => (a === 'write' ? 'warning' : 'info')

interface IProps<T> {
  open: boolean
  user: IUser | null
  fields: { key: string; column: string; isTag: boolean }[]
  onClose: () => void
  fetchUsers: () => Promise<void>
  isLightMode: boolean
  globalRolesWithPermissions: IRole[]
}

const UserViewModal = <T extends Record<string, any>>({
  open,
  user,
  fields,
  onClose,
  fetchUsers,
  isLightMode,
  globalRolesWithPermissions,
}: IProps<T>) => {
  const [userData, setUserData] = useState<IUser | null>(null)
  const [userRoles, setUserRoles] = useState<IRole[]>([])
  const [pendingRole, setPendingRole] = useState<string | null>(null)
  const [availableRoles, setAvailableRoles] = useState<IRole[]>([])
  const [tiers, setTiers] = useState<ITier[]>([])
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [passwordError, setPasswordError] = useState(false)
  const [confirmationError, setConfirmationError] = useState(false)

  const isNewUser = !userData?.id

  const { showToast } = useToast()

  const handlePendingRoleSelect = (roleName: string) => {
    const matchedRole = globalRolesWithPermissions.find((r) => r.name === roleName)
    if (matchedRole) {
      setUserRoles((prev) => [...prev, matchedRole])
      setAvailableRoles((prev) => prev.filter((r) => r.name !== roleName))
    }
    setPendingRole(null)
  }

  const handleDeleteRole = (roleName: string) => {
    setUserRoles((prev) => prev.filter((r) => r.name !== roleName))
    const deleted = globalRolesWithPermissions.find((r) => r.name === roleName)
    if (deleted) {
      setAvailableRoles((prev) => [...prev, deleted])
    }
  }

  const handleSubmit = async () => {
    if (!userData) return

    if (isNewUser) {
      if (password.length < 8) {
        setPasswordError(true)
        showToast('error', 'Password must be at least 8 characters')
        return
      }
      if (password !== passwordConfirmation) {
        setConfirmationError(true)
        showToast('error', 'Password confirmation does not match')
        return
      }
    }
    setPasswordError(false)
    setConfirmationError(false)

    const payload = isNewUser ? { ...userData, password, password_confirmation: passwordConfirmation } : userData
    const data = await updateUserData(payload, userRoles)
    if (data.success) {
      showToast('success', `User ${isNewUser ? 'created' : 'updated'} successfully`)
      fetchUsers()
    }
  }

  const handleUserInfoChange = (key: string, value: string | number | null) => {
    if (userData === null) {
      setUserData({ [key]: value } as unknown as IUser)
      return
    }
    setUserData({ ...userData, [key]: value })
  }

  const handleRoleAdd = () => {
    if (availableRoles.length > 0) {
      setPendingRole('')
    }
  }

  const fetchTiers = async () => {
    const body = await getAllTiers()

    if (body.data) {
      setTiers(body.data.tiers)
    }
  }

  useEffect(() => {
    if (user) {
      setUserData(user)
      // The user-list endpoint loads roles but not their permissions, so swap
      // each assigned role for its full counterpart from the global list.
      const globalById = new Map(globalRolesWithPermissions.map((r) => [r.id, r]))
      const assignedRoles = (user.roles || []).map(
        (r) => globalById.get(r.id) ?? { ...r, permissions: r.permissions ?? [] },
      )
      setUserRoles(assignedRoles)

      const assignedRoleIds = new Set(assignedRoles.map((r) => r.id))
      setAvailableRoles(globalRolesWithPermissions.filter((r) => !assignedRoleIds.has(r.id)))

      setPassword('')
      setPasswordConfirmation('')
      setPasswordError(false)
      setConfirmationError(false)
    }
  }, [user, globalRolesWithPermissions])

  useEffect(() => {
    setUserData(user)
  }, [user])

  useEffect(() => {
    fetchTiers()
  }, [])

  return (
    <Modal className={styles.modal} open={open} onClose={onClose}>
      <Box className={styles.modalContent}>
        <Box className={styles.content}>
          <Box
            className={styles.header}
            sx={{
              borderBottom: isLightMode ? '1px solid black' : '1px solid white',
            }}
          >
            <Typography className={styles.title}>User Information</Typography>
            <IconButton onClick={onClose}>
              <CloseOutlinedIcon />
            </IconButton>
          </Box>

          <Box className={styles.fields}>
            {fields.slice(1, 4).map((field) => (
              <Box key={field.key} className={styles.fieldContainer}>
                <Typography>{field.column}:</Typography>
                <TextField
                  value={(userData as any)?.[field.key] || ''}
                  className={styles.field}
                  size="small"
                  onChange={(e) => handleUserInfoChange(field.key, e.target.value)}
                />
              </Box>
            ))}
            <Box className={styles.fieldContainer}>
              <Typography>Tier:</Typography>
              <SelectWithSearch
                width={300}
                value={thru(
                  (tiers || []).find((tier) => (userData?.tiers_by_level || {})[tier.level] === tier.id),
                  (tier) => ({
                    label: `${tier.level} (${tier.name})`,
                    value: tier.id,
                  }),
                )}
                label="Tier"
                handleChange={(value) => {
                  const tier = (tiers || []).find((t) => t.id === Number(value))
                  if (tier) handleUserInfoChange('tiers_by_level', { [tier.level]: tier.id } as any)
                }}
                options={(tiers || []).map((tier) => ({
                  value: tier.id,
                  label: `${tier.level} (${tier.name})`,
                }))}
              />
            </Box>
            {isNewUser && (
              <>
                <Box className={styles.fieldContainer}>
                  <Typography>Password:</Typography>
                  <TextField
                    type="password"
                    value={password}
                    error={passwordError}
                    helperText={passwordError ? 'Must be at least 8 characters' : undefined}
                    onChange={(e) => {
                      setPassword(e.target.value)
                      if (passwordError) setPasswordError(false)
                    }}
                    className={styles.field}
                    size="small"
                  />
                </Box>
                <Box className={styles.fieldContainer}>
                  <Typography>Password Confirmation:</Typography>
                  <TextField
                    type="password"
                    value={passwordConfirmation}
                    error={confirmationError}
                    helperText={confirmationError ? 'Does not match password' : undefined}
                    onChange={(e) => {
                      setPasswordConfirmation(e.target.value)
                      if (confirmationError) setConfirmationError(false)
                    }}
                    className={styles.field}
                    size="small"
                  />
                </Box>
              </>
            )}
          </Box>

          <Box
            className={styles.header}
            sx={{
              borderBottom: isLightMode ? '1px solid black' : '1px solid white',
              marginTop: 5,
            }}
          >
            <Typography className={styles.title}>User Roles</Typography>
            {pendingRole !== null ? (
              <TextField
                select
                label="Select Role"
                value={pendingRole}
                onChange={(e) => handlePendingRoleSelect(e.target.value)}
                size="small"
                className={styles.roleSelect}
              >
                {availableRoles.map((role) => (
                  <MenuItem key={role.id} value={role.name}>
                    {role.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Button className={styles.roleBtn} onClick={handleRoleAdd}>
                Add Role
                <AddIcon />
              </Button>
            )}
          </Box>

          <Box className={styles.rolesList}>
            {userRoles.map((role) => (
              <Accordion key={role.name}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ flexGrow: 1 }}>{role.name}</Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDeleteRole(role.name)
                    }}
                  >
                    <DeleteIcon sx={{ color: 'red' }} fontSize="small" />
                  </IconButton>
                </AccordionSummary>
                <AccordionDetails className={styles.permissions}>
                  {(() => {
                    const grouped = role.permissions.reduce<Record<string, typeof role.permissions>>((acc, p) => {
                      ;(acc[p.resource_type] ||= []).push(p)
                      return acc
                    }, {})

                    return Object.entries(grouped).map(([resource, perms]) => (
                      <Box
                        key={resource}
                        sx={{
                          mb: 2,
                          p: 1.5,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Chip label={resource} size="small" color="primary" variant="outlined" />
                            <Chip
                              label={`${perms.length} permission${perms.length > 1 ? 's' : ''}`}
                              size="small"
                              variant="outlined"
                            />
                          </Stack>
                        </Stack>

                        <Divider sx={{ mb: 1 }} />

                        <Stack spacing={0.75}>
                          {perms
                            .sort((a, b) => a.action.localeCompare(b.action))
                            .map((p) => (
                              <Box
                                key={p.id}
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: 'auto auto 1fr auto',
                                  gap: 1,
                                  alignItems: 'center',
                                  p: 0.75,
                                  borderRadius: 1,
                                  bgcolor: 'action.hover',
                                }}
                              >
                                <Chip size="small" label={p.action} color={actionColor(p.action)} />

                                <Tooltip
                                  title={<pre style={{ margin: 0 }}>{scopeFull(p.scope)}</pre>}
                                  placement="top"
                                  arrow
                                >
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={p.resource_type === 'tiers' ? 'No scope required' : scopeShort(p.scope)}
                                    sx={{ maxWidth: 380 }}
                                  />
                                </Tooltip>

                                <Typography variant="caption" color="text.secondary" sx={{ justifySelf: 'end' }}>
                                  #{p.id}
                                </Typography>
                              </Box>
                            ))}
                        </Stack>
                      </Box>
                    ))
                  })()}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          <Box className={styles.submitBtnContainer}>
            <Button onClick={handleSubmit}>Submit</Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}

export default UserViewModal
